import { ScrEnvironment } from './../../environments/environment';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { IScrBaseResponse, ScrBaseResponse, ScrBaseService } from './base.service';
import { ScrServiceManager } from './servicemanager.service';

import { pdfDownload, WITHCRED, WithCredNoCache } from './authentication.service';
import {
  MonthEndReport, BrandCountObj, BrandIdReportRequestPayload,
  BrandIdReportResponse, ConsumerReportResponse, ComplaintReportRequest
} from '../models/industry-reports.model';
import { formatDate } from '@angular/common';

export class MonthEndListResponse extends ScrBaseResponse {
  data!: MonthEndReport[];
}
export class BrandCountResponse extends ScrBaseResponse {
  data!: BrandCountObj;

}


export interface ExpandedDetails {
  email?: string,
  fein?: string,
  firstName?: string,
  lastName?: string,
  phone?: string,
  phoneExtension?: string,
  country: string;
  companyName?: string,
  legalName?: string,
  vetInsights: VetInsight[]
}

export interface VetInsight {
  finding?: string;
  notificationDate?: string;
  determination?: string;
}

export class VettingInsightsResults {
  accountId?: string;
  vetType?: string;
  customerSince?: string;
  leasedShortCodesCount?: number;
  vettingInsightsCount?: number;
  expandedDetails!: ExpandedDetails;
  uniqueId!: number;
}

export class VettingInsightsResultsResponse {
  data!: { content: VettingInsightsResults[] };
}

class ExpandedDetailsResponse {
  data!: ExpandedDetails;
}

export class RiskTypeResponse extends ScrBaseResponse {
  riskTypes!: string[];
}

export class ComplaintReportResponse extends ScrBaseResponse {
  data!: {
    content: ConsumerReportResponse[];
    totalCount: number;
    limit: number;
  };
}

export interface VettingInsightsFilters {
  companyName?: string;
  shortCode?: string;
  vetTypeChoices: string[];
  findingsChoices?: string[];
  startDate: Date;
  endDate?: Date;
}

export interface VmAuditSummary {
  auditId: number;
  formId: string;
  noticeDate: string;
  shortCode: string;
  overallSeverity: string;
  status: string;
  auditType: string;
  cureDate: string;
  sourceType: string;
  fileName: string;
}
export interface AuditResults {
  audits: VmAuditSummary[];
  page: number;
  limit: number;
  totalCount: number;

}
export interface AuditResultsResponse extends IScrBaseResponse {
  data: AuditResults;
}


export interface ComplaintSummary {
  rickType: string;
  amount: number;
  percentage: string;
}


export interface ComplainSummaryRequest {
  selection: string;
  startDate?: string;
  endDate?: string;
}
export interface ComplainSummaryResponse extends IScrBaseResponse {
  summary: ComplaintSummary[];
}



export interface InMarketMonitoringFilter {
  status?: string[];
  auditType?: string[];
  startDate?: Date;
  endDate?: Date;
  formId?: string;
  shortCode?: string;
  page: number;
}


@Injectable({
  providedIn: 'root'
})
export class IndustryReportsService extends ScrBaseService {

  constructor(
    serviceManager: ScrServiceManager,
    private environment: ScrEnvironment,
    private http: HttpClient) {
    super(serviceManager);
  }

  public fetchInMarketReports(filter: InMarketMonitoringFilter): Observable<AuditResultsResponse> {

    let params = new HttpParams();
    if (filter.auditType && filter.auditType.length > 0) {
      params = params.set('auditType', filter.auditType.join());
    }
    if (filter.status && filter.status.length > 0) {
      params = params.set('status', filter.status.join());
    }
    if (filter.startDate) {
      const fmt = formatDate(filter.startDate, 'yyyy-MM-dd', 'en-US') ?? '';
      params = params.set('startDate', fmt);
    }
    if (filter.endDate) {
      const fmt = formatDate(filter.endDate, 'yyyy-MM-dd', 'en-US') ?? '';
      params = params.set('endDate', fmt);
    }
    if (filter.formId) {
      params = params.set('formId', filter.formId);
    }
    if(filter.page){
      params = params.set('page',filter.page);
    }
    if (filter.shortCode) {
      params = params.set('shortCode', ScrBaseService.shortCodeToNumeric(filter.shortCode));
    }

    const url = `${this.environment.base}/secure2/industryreports/inmarketmonitoring`;
    return this.http.get<AuditResultsResponse>(url, { params, ...WithCredNoCache });
  }

  fetchCccReports(req: ComplaintReportRequest): Observable<ComplaintReportResponse> {

    const url = `${this.environment.base}/secure2/industryreports/complaintreport`;
    return this.http.post<ComplaintReportResponse>(url, req, { ...WithCredNoCache });
  }

  getMonthEndReports(): Observable<MonthEndListResponse> {
    const url = `${this.environment.base}/secure2/industryreports/monthendreport`;
    return this.http.get<MonthEndListResponse>(url, WithCredNoCache);
  }

  getRiskTypes(): Observable<RiskTypeResponse> {
    const url = `${this.environment.base}/secure2/industryreports/complaint/risktypes`;
    return this.http.get<RiskTypeResponse>(url, WithCredNoCache);
  }

  downloadReport(id: number): Observable<HttpResponse<Blob>> {
    const url = `${this.environment.base}/secure2/industryreports/inmarketmonitoring/download?auditId=${id}`;
    return this.http.get<HttpResponse<Blob>>(url, pdfDownload);
  }

  downloadMonthEndReport(id: string): Observable<HttpResponse<Blob>> {
    const url = `${this.environment.base}/secure2/industryreports/monthendreport/download/${id}`;
    return this.http.get<HttpResponse<Blob>>(url, pdfDownload);
  }

  getBrandIdentificationFilters(): Observable<BrandCountResponse> {
    const url = `${this.environment.base}/secure2/industryreports/brandidcounts`;
    return this.http.get<BrandCountResponse>(url, WithCredNoCache);
  }

  downloadBrandIdentificationHistory(): Observable<HttpResponse<Blob>> {
    const url = `${this.environment.base}/secure2/industryreports/brandidreport/download`;
    return this.http.get<HttpResponse<Blob>>(url, pdfDownload);
  }

 /* downloadComplaintClassification(): Observable<HttpResponse<Blob>> {
    const url = `${this.environment.base}/secure2/industryreports/complaint/downloadsummary`;

    return this.http.get<HttpResponse<Blob>>(url, pdfDownload);
  }*/

  downloadComplaintClassification(): Observable<HttpResponse<Blob>> {
    const url = `${this.environment.base}/fct/industryreports/complaint/downloadsummary`;
    const body = this.functionUrlBody('downloadSummary',{action:'downloadSummary'});
    return this.http.post<HttpResponse<Blob>>(url,body, pdfDownload);

  
    
     // const body = this.functionUrlBody('downloadSummary', {req});
       
      //return this.http.post<ComplaintReportResponse>(url, body, { ...WithCredNoCache });
  
  }
    

  generateBrandIdentificationReport(payload: BrandIdReportRequestPayload): Observable<BrandIdReportResponse> {
    const url = `${this.environment.base}/secure2/industryreports/brandidreport`;
    return this.http.post<BrandIdReportResponse>(url, payload, WithCredNoCache);
  }

  getComplaintSummary(selection?: string, startDate?: string, endDate?: string): Observable<ComplainSummaryResponse> {
    let params = new HttpParams();
    if (selection) {
      params = params.set('selection', selection);
    }
    if (startDate) {
      params = params.set('startDate', startDate);
    }
    if (endDate) {
      params = params.set('endDate', endDate);
    }
    const url = `${this.environment.base}/secure2/industryreports/complaint/summary`;
    return this.http.get<ComplainSummaryResponse>(url, { params, ...WithCredNoCache });
  }

  getVettingInsightSummary(params: VettingInsightsFilters): Observable<VettingInsightsResultsResponse> {
    const url = `${this.environment.base}/secure2/industryreports/vettinginsights`;
    return this.http.post<VettingInsightsResultsResponse>(url, params, WITHCRED);
  }

  getVettingInsightAccount(accountId: string, companyName: string, vetType: string): Observable<ExpandedDetailsResponse> {
    //Roundabout way just incase undefined causes issue for initial testing.
    let params;
    if (accountId && companyName && vetType) {
      params = { accountId, companyName, vetType };
    } else if (companyName && vetType ) {
      params = { companyName, vetType };
    } else if (accountId) {
      params = { accountId };
    }
    const url = `${this.environment.base}/secure2/industryreports/vettinginsights/account`;
    return this.http.get<ExpandedDetailsResponse>(url, { params, ...WithCredNoCache });
  }
}
